import { useState, useEffect, useCallback } from 'react';
import { WrapperEntitySelector, UserLabelsSelection } from '@components/Inputs';
import { GridBreak } from '@components/Utils';
import Misc from '@components/Misc';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { Grid, TextInput, Select, Checkbox, Tabs } from '@ts/storybook';
import { IForm } from '@ts/ui-models';
import { WithTranslation, withTranslation } from 'react-i18next';

interface ITaskTypeFormProps extends IForm, WithTranslation {}

const TaskTypeForm = ({ values, mode, onChange, requiredFields, t }: ITaskTypeFormProps) => {
  const [activeTab, setActiveTab] = useState<'main' | 'event'>('main');
  const isViewMode = mode === 'view';

  useEffect(() => {
    if (values.limitedToItemTypeId) {
      onChange('itemTypeLimited', true);
    }
  }, [values.limitedToItemTypeId, onChange]);

  const onChangeValue = useCallback(
    (attr: string) => (value: any) => {
      onChange(attr, value);
    },
    [onChange]
  );

  const handleChangeEntitySelector = useCallback(
    (entity: any, entityName: string, fkField: string) => {
      const value = entity ? entity.getID() : null;
      onChange(entityName, entity);
      onChange(fkField, value);
    },
    [onChange]
  );

  const renderMandatoryInfo = () => {
    const responsibleUserEntity = values.responsibleUser;

    return (
      <Grid container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Misc.PaperSectionTitle>{t('Basic Information')}</Misc.PaperSectionTitle>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput
            isReadOnly={isViewMode}
            label="Name"
            value={values.name}
            onChangeValue={onChangeValue('name')}
            required={requiredFields.includes('name')}
            infoText="Blank spaces at the beginning and the end will be trimmed on save"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput
            isReadOnly={isViewMode}
            label="Custom ID"
            value={values.customId}
            onChangeValue={onChangeValue('customId')}
            infoText="Blank spaces at the beginning and the end will be trimmed on save"
            type="ID"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <WrapperEntitySelector
            label={'Assignee'}
            searchComponent={generateSearchEntitySelector('user')}
            value={responsibleUserEntity ?? values.responsibleUserId}
            onChangeEntity={entity => handleChangeEntitySelector(entity, 'responsibleUser', 'responsibleUserId')}
            isReadOnly={isViewMode}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <UserLabelsSelection
            key="addressedlabels"
            label={'Addressed to following user groups'}
            value={values.userGroups}
            onChange={onChangeValue('userGroups')}
            isReadOnly={isViewMode}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput isReadOnly={isViewMode} label="Notes" value={values.notes} onChangeValue={onChangeValue('notes')} />
        </Grid>
      </Grid>
    );
  };

  const renderOptionalInfo = () => {
    const menuItems = Array.from({ length: 11 }, (_, num) => ({
      value: num.toString(),
      label: num.toString(),
    }));

    return (
      <Grid container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Misc.PaperSectionTitle>{t('Optional Information')}</Misc.PaperSectionTitle>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <TextInput
            label="Warning before"
            value={values.warningBefore}
            onChangeValue={onChangeValue('warningBefore')}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            isReadOnly={isViewMode}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Select
            value={values.warningBeforeUnits}
            onChange={onChangeValue('warningBeforeUnits')}
            disabled={isViewMode}
            options={[
              { value: 'days', label: 'days' },
              { value: 'hours', label: 'hours' },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Checkbox
            label={'Create successive task upon completion of every task'}
            checked={values.successiveTask || false}
            onChangeValue={value => {
              onChange('successiveTask', value);
              if (value === false) {
                onChange('periodicity', null);
              }
            }}
            disabled={isViewMode}
          />
        </Grid>
        {values.successiveTask && (
          <>
            <Grid size={{ xs: 12 }}>
              <Checkbox
                label="Use calendar days"
                checked={values.periodicityCalendarDays}
                onChangeValue={onChangeValue('periodicityCalendarDays')}
                disabled={isViewMode}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextInput
                label="Periodicity"
                value={values.periodicity}
                onChangeValue={onChangeValue('periodicity')}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  readOnly: isViewMode || !values.successiveTask,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Select
                value={values.periodicityUnits}
                onChange={onChangeValue('periodicityUnits')}
                disabled={isViewMode}
                options={[
                  { value: 'days', label: 'days' },
                  { value: 'hours', label: 'hours' },
                ]}
              />
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12 }}>
          <Checkbox
            label="Create an independent task for each item"
            checked={values.separatedTask || false}
            onChangeValue={onChangeValue('separatedTask')}
            disabled={isViewMode || mode !== 'new'}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Checkbox
            label="Restrict (only applicable) to a specific item type"
            checked={values.itemTypeLimited || false}
            onChangeValue={onChangeValue('itemTypeLimited')}
            disabled={isViewMode}
          />
        </Grid>
        {values.itemTypeLimited && (
          <Grid size={{ xs: 6 }}>
            <WrapperEntitySelector
              label={'Item type limited to'}
              searchComponent={generateSearchEntitySelector('itemType')}
              value={values.limitedToItemType ?? values.limitedToItemTypeId}
              onChangeEntity={entity => handleChangeEntitySelector(entity, 'limitedToItemType', 'limitedToItemTypeId')}
              isReadOnly={isViewMode}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            label="Mandatory number of people"
            value={values.numberOfPeople || ''}
            onChange={onChangeValue('numberOfPeople')}
            disabled={isViewMode}
            options={menuItems}
          />
        </Grid>
        <GridBreak />
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            label="Mandatory number of items"
            value={values.numberOfItems || ''}
            onChange={onChangeValue('numberOfItems')}
            disabled={isViewMode}
            options={menuItems}
          />
        </Grid>
      </Grid>
    );
  };

  const renderMain = () => (
    <Misc.NewPaperWithPadding>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>{renderMandatoryInfo()}</Grid>
        <Grid size={{ xs: 12 }}>{renderOptionalInfo()}</Grid>
      </Grid>
    </Misc.NewPaperWithPadding>
  );

  const renderEventLogging = () => (
    <Misc.NewPaperWithPadding>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Misc.PaperSectionTitle>{t('Event logging')}</Misc.PaperSectionTitle>
        </Grid>
        {/* Implementar campos de registro de eventos aquí */}
      </Grid>
    </Misc.NewPaperWithPadding>
  );

  const renderContents = () => {
    switch (activeTab) {
      case 'main':
        return renderMain();
      case 'event':
        return renderEventLogging();
      default:
        return null;
    }
  };

  const tabs = [
    { label: 'Main', value: 'main' },
    { label: 'Event logging', value: 'event', disabled: true },
  ];

  return (
    <Tabs id="task-type-page" tabs={tabs} value={activeTab} onChange={(_, tab) => setActiveTab(tab)}>
      {renderContents()}
    </Tabs>
  );
};

export default withTranslation()(TaskTypeForm);
